import { useMemo, useState, useEffect } from "react";
import useSeo from "@/hooks/useSeo";
import NewsCard from "@/components/feature/NewsCard";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function NewsList() {
  useSeo({
    title: "NBA直播吧 - 赛事新闻",
    description:
      "NBA赛事新闻聚合页，汇总最新NBA战报、赛前前瞻、数据解读、伤病动态与新秀观察等资讯，帮助球迷了解联盟最新动态。",
    keywords: "NBA新闻,NBA赛事新闻,NBA战报,NBA前瞻,NBA资讯",
  });

  const categories = useMemo(() => ["全部", ...Array.from(new Set(newsList.map((n) => n.category)))], []);
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => newsList.filter((n) => category === "全部" || n.category === category),
    [category]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-foreground-400 font-label mb-2">
          <i className="ri-home-4-line"></i> 首页 <i className="ri-arrow-right-s-line"></i> <span className="text-foreground-600">赛事新闻</span>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">赛事新闻</h1>
        <p className="text-sm text-foreground-500 mt-1">NBA战报、前瞻、数据解读与联盟动态，第一时间掌握赛事资讯</p>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              setCategory(c);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-full text-xs font-label whitespace-nowrap transition-colors cursor-pointer ${
              category === c ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {pageItems.length === 0 ? (
        <div className="bg-background-50 rounded-lg border border-background-200 py-20 flex flex-col items-center gap-3 text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-newspaper-line"></i>
          </span>
          <p className="text-foreground-900 font-medium">暂无新闻报道</p>
          <p className="text-sm text-foreground-500">该分类下暂时没有新闻内容，试试其他分类</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {pageItems.map((n) => (
              <NewsCard key={n.id} news={n} variant="horizontal" />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
                  page === 1 ? "bg-background-100 text-foreground-300 cursor-not-allowed" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`w-9 h-9 rounded-md text-sm font-label cursor-pointer transition-colors ${
                    page === i + 1 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={`w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
                  page === totalPages ? "bg-background-100 text-foreground-300 cursor-not-allowed" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}